import React from 'react';
import { Box, Chip, Divider, Grid, IconButton, MenuItem, MenuList, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { ICandidateInterview } from '../InterviewsTab';
import moment from 'moment';
import { CustomTooltip } from '../../../../../Common/Tooltips/CustomTooltip';
import InterviewIcon from 'app/assets/images/CandidateProfile/InterviewIcon.svg';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ff_interviewViewHistory } from 'app/constants/FeatureFlags/Candidate/Keys';
import { useDecision } from '@optimizely/react-sdk';
import { useSelector } from 'react-redux';
import { disciplineDataSelector } from 'store/redux-store/lookup/lookup.selectors';
import { theme } from 'styles/global-styles';

interface InterviewTabProps {
  addInterview?: boolean;
  data: ICandidateInterview;
  handleClick: (data: ICandidateInterview) => void;
}

const useStyles = makeStyles()(theme => ({
  container: {
    width: '100%',
    border: '1px solid #ececec',
    borderRadius: '4px',
    padding: '15px 10px 15px 25px;',
    minHeight: '328px',
  },
  divider: {
    margin: '5px 20px 10px 0px',
  },
  child: {
    overflow: 'hidden',
    fontSize: '14px',
  },
  subTitle: {
    fontWeight: 'bold',
  },
  fontAdjustment: {
    fontSize: '12px',
  },
  chip: {
    fontWeight: 500,
    fontSize: '14px',
  },
  icon: {
    height: '25px',
    width: '25px',
  },
  tooltipPlacementBottom: {
    margin: 0,
    marginTop: 22,
    padding: '8px 8px',
    fontSize: '14px',
    color: '#000',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  menuItem: {
    '&:hover': {
      backgroundColor: '#fff',
    },
  },
}));

export const RenderWithTooltip = ({
  text,
  maxLength = 30,
  className,
}: {
  text?: string;
  maxLength?: number;
  className?: string;
}) => {
  if (!text || text.trim() === '') {
    return <Box className={className}>-</Box>;
  }

  return text.length < maxLength ? (
    <Box className={className}>{text}</Box>
  ) : (
    <CustomTooltip
      contentStyle={{
        overflow: 'hidden',
        color: 'black',
        fontSize: '14px',
        fontWeight: 'normal',
      }}
      tooltipContent={text}
      value={`${text.slice(0, maxLength - 7)}...`}
    />
  );
};

export const RenderListWithTooltip = ({
  values,
  maxLength = 30,
  className,
}: {
  values?: string[];
  maxLength?: number;
  className?: string;
}) => {
  if (!values || values.length === 0) {
    return <Box className={className}>-</Box>;
  }

  const joined = values.join(', ');
  const shouldTruncate = joined.length >= maxLength;

  const tooltipContent = (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {values.map((item, index) => (
        <li key={index} style={{ fontSize: '12px', color: 'black' }}>
          {item}
        </li>
      ))}
    </ul>
  );

  return shouldTruncate ? (
    <CustomTooltip
      contentStyle={{
        overflow: 'hidden',
        color: 'black',
        fontSize: '14px',
        fontWeight: 'normal',
      }}
      tooltipContent={tooltipContent}
      value={`${joined.slice(0, maxLength - 7)}...`}
    />
  ) : (
    <Box className={className}>{joined}</Box>
  );
};

export const colorTokenMap: Record<string, string> = {
  // Chip border / text
  brightNavyBlue: theme.palette.framework.system.vividSkyBlue,
  kellyGreen: theme.palette.framework.system.deepForestGreen,
  red: theme.palette.framework.system.red,
  orange: theme.palette.framework.chart.orange,
  tertiaryGrey: theme.palette.framework.system.tertiaryGrey,

  // Chip backgrounds (light versions)
  lightBlue: theme.palette.framework.system.powderBlue,
  mint: theme.palette.framework.system.translucentForestGreen,
  paleRed: theme.palette.framework.system.translucentCrimson,
  lightOrange: theme.palette.framework.system.sunsetOrange,
  platinum: theme.palette.framework.system.platinum,

  // Summary card icon backgrounds (mapped to existing theme colours)
  yellowExclamation: theme.palette.framework.system.lightOrange, // reuse lightOrange
  greenTick: theme.palette.framework.system.mint, // reuse mint
};

export const InterviewCard = (props: InterviewTabProps) => {
  const { data, handleClick } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [interviewViewHistory] = useDecision(ff_interviewViewHistory);
  const disciplineData = useSelector(disciplineDataSelector);

  const getSkillset = () => {
    const discipline = disciplineData.find(disc => disc.object.Abbreviation === data.disciplineAbbr)?.object;
    const renderText = discipline?.Description
      ? data.specialtyAbbr
        ? `${discipline.Description} - ${data.specialtyAbbr}`
        : discipline?.Description
      : '-';
    return renderText;
  };

  const chipData = {
    color: colorTokenMap[data?.candidateInterviewStatus?.color] || colorTokenMap.tertiaryGrey,
    background: colorTokenMap[data?.candidateInterviewStatus?.background] || colorTokenMap.platinum,
    label: data?.candidateInterviewStatus?.label || 'Unavailable',
  };

  const TooltipContent = () => {
    return (
      <MenuList style={{ padding: 0 }}>
        <MenuItem className={classes.menuItem} onClick={() => handleClick(data)} data-testid="view-history-tooltip">
          <div style={{ fontSize: '12px' }}>{t('candidate.virtualInterviewTabLabels.viewHistory')}</div>
        </MenuItem>
      </MenuList>
    );
  };

  const mostRecentReason = data.candidateInterviewReasons?.find(r => r.isMostRecent);

  return (
    <Paper className={classes.container}>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Grid container spacing={2} direction="row" xs={12} style={{ height: '60px' }}>
            <Grid item xs={1}>
              <img src={InterviewIcon} className={classes.icon} />
            </Grid>
            <Grid item xs={11}>
              <Grid container spacing={2} direction="row" justifyContent="space-between">
                <Grid item>
                  <Grid container spacing={0} direction="column">
                    <Grid item>
                      <Box className="page-title">
                        {data.interviewDate ? moment(new Date(data.interviewDate)).format('MM/DD/YYYY') : '-'}
                      </Box>
                    </Grid>
                    <Grid item>
                      <Box className={classes.fontAdjustment}>{`Invite ${data.inviteId ? data.inviteId : '-'} ${
                        data.invitationDate ? moment(new Date(data.invitationDate)).format('MM/DD/YYYY') : ' '
                      }`}</Box>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container direction="row" alignItems="center" spacing={1}>
                    <Grid item>
                      <CustomTooltip
                        tooltipContent={data.candidateInterviewStatus?.interviewStatus ?? ''}
                        interactive
                        arrow
                      >
                        <Chip
                          className={classes.chip}
                          style={{
                            color: chipData?.color,
                            borderColor: chipData?.color,
                            background: chipData?.background,
                          }}
                          variant="outlined"
                          size="medium"
                          label={chipData?.label}
                        />
                      </CustomTooltip>
                    </Grid>

                    {interviewViewHistory.enabled && (
                      <Grid item>
                        <IconButton color="primary" style={{ padding: '0', marginLeft: '10px' }} size="small">
                          <CustomTooltip
                            arrow
                            style={{ color: '#888888' }}
                            tooltipContent={<TooltipContent />}
                            tooltipPlacementBottom={classes.tooltipPlacementBottom}
                            interactive
                            placement="bottom"
                          >
                            <MoreVertIcon />
                          </CustomTooltip>
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider className={classes.divider}></Divider>
        <Grid item>
          <Grid container spacing={3} direction="row" justifyContent="space-between" xs={12}>
            <Grid item xs={5} className={classes.child}>
              <Grid container spacing={1} direction="column">
                <Grid item>
                  <Box className={classes.subTitle}>{t('candidate.virtualInterviewTabLabels.skillset')}</Box>
                </Grid>
                <Grid item>
                  <Box>{getSkillset()}</Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={5} className={classes.child}>
              <Grid container spacing={1} direction="column">
                <Grid item>
                  <Box className={classes.subTitle}>{t('candidate.virtualInterviewTabLabels.rating')}</Box>
                </Grid>
                <Grid item>
                  <Box>
                    {data.interviewRating !== ''
                      ? data.interviewRating.substring(0, data.interviewRating.length - 1)
                      : '-'}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={5} className={classes.child}>
              <Grid container spacing={1} direction="column">
                <Grid item>
                  <Box className={classes.subTitle}>{t('candidate.virtualInterviewTabLabels.statusReason')}</Box>
                </Grid>
                <Grid item>
                  <RenderWithTooltip text={mostRecentReason?.reason} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={5} className={classes.child}>
              <Grid container spacing={1} direction="column">
                <Grid item>
                  <Box className={classes.subTitle}>{t('candidate.virtualInterviewTabLabels.subStatus')}</Box>
                </Grid>
                <Grid item>
                  <RenderListWithTooltip values={mostRecentReason?.subReasons} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={5} className={classes.child}>
              <Grid container spacing={1} direction="column">
                <Grid item>
                  <Box className={classes.subTitle}>{t('candidate.virtualInterviewTabLabels.lastUpdated')}</Box>
                </Grid>
                <Grid item>
                  <Box>{data.changedDate ? moment(new Date(data.changedDate)).format('MM/DD/YYYY') : '-'}</Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};
